import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CCard, CCardBody, CCardHeader } from '@coreui/react-pro'
import { Table, TableProps } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
  path,
  useColumnLocalFilter,
  useColumnSearch,
  useTableSearchParams,
} from 'src/libs'
import { ErrorScreen } from 'src/shared/components/errorScreen/errorScreen'
import { setCopytradeHistoryRequestError } from './model'
import { useCopytradeHistoryWidget } from './hooks'
import { CopytradeHistory, Strategy, Account } from './types'

export const CopytradeHistoryWidget = () => {
  return (
    <CCard>
      <CCardHeader>
        <strong>Copytrade History</strong>
      </CCardHeader>
      <CCardBody>
        <CopytradeHistoryList />
      </CCardBody>
    </CCard>
  )
}

const CopytradeHistoryList = () => {
  return <CopytradeHistoryTable />
}

const CopytradeHistoryTable = () => {
  const [searchParams] = useSearchParams()
  const {
    copytradeHistory,
    loading,
    totalCountOfCopytradeHistory,
    onChangeCopytradeHistoryList,
    errorCopytradeHistoryRequest,
  } = useCopytradeHistoryWidget()

  const getFilters = (): Record<string, string> => {
    const filters: Record<string, string> = {}
    const accountMtLogin = searchParams.get('searchField_account')
    if (accountMtLogin) {
      filters['account.mtLogin'] = accountMtLogin
    }

    const strategyTitle = searchParams.get('searchField_strategy.title')
    if (strategyTitle) {
      filters['strategy.title'] = strategyTitle
    }

    const deletedAt = searchParams.get('filterField_deletedAt')
    if (deletedAt) {
      filters['deletedAt'] = deletedAt
    }

    return filters
  }

  const searchFields = ['account', 'strategy.title']
  const filterFields = ['deletedAt']

  const handleTableChange: TableProps<CopytradeHistory>['onChange'] =
    useTableSearchParams({
      searchFields,
      filterFields,
      usePagination: true,
    })

  useEffect(() => {
    onChangeCopytradeHistoryList({
      limit: Number(searchParams.get('limit')) || 10,
      page: Number(searchParams.get('page')) || 1,
      filters: getFilters(),
    })
  }, [searchParams])

  const accountSearch = useColumnSearch<CopytradeHistory>({
    dataIndex: 'account',
    placeholder: 'Account mtLogin',
  })

  const strategySearch = useColumnSearch<CopytradeHistory>({
    dataIndex: ['strategy', 'title'],
    placeholder: 'CT Strategy',
    customRender: (highlightedValue, record) => (
      <a
        href={path.copytrade.strategies.details(String(record.strategy.id))}
        target="_blank"
        rel="noreferrer"
        style={{ textDecoration: 'none' }}
      >
        {highlightedValue}
      </a>
    ),
  })

  const columns: ColumnsType<CopytradeHistory> = [
    {
      title: 'Account',
      dataIndex: 'account',
      ...accountSearch,
      render: (account: Account) => account.mtLogin,
    },
    {
      title: 'Account type',
      dataIndex: 'account',
      render: (account: Account) =>
        account?.accountPlatform + ' ' + account?.tariff,
    },
    {
      title: 'Account leverage',
      render: (account: Account) => account?.leverage,
    },
    {
      title: 'CT Strategy',
      ...strategySearch,
      dataIndex: ['strategy', 'title'],
    },
    {
      title: 'Strategy type',
      dataIndex: 'strategy',
      render: (item: Strategy) =>
        item?.account?.accountPlatform + ' ' + item?.account?.tariff,
    },
    {
      title: 'Strategy Leverage',
      dataIndex: 'strategy',
      render: (item: Strategy) => item.account.leverage,
    },
    {
      title: 'Status',
      dataIndex: 'deletedAt',
      ...useColumnLocalFilter<CopytradeHistory>({ dataIndex: 'deletedAt' }),
      render: (deletedAt: string, record: CopytradeHistory) => {
        if (deletedAt) {
          return 'closed'
        }

        return 'active'
      },
      filterMultiple: false,
      filters: [
        { text: 'Active', value: 'null' },
        { text: 'Closed', value: 'not null' },
      ],
    },
    {
      title: 'Proportion',
      dataIndex: 'proportion',
    },
    {
      title: 'Commission',
      dataIndex: 'commission',
    },
    {
      title: 'Started',
      dataIndex: 'startedAt',
      render: (startedAt: string, record: CopytradeHistory) =>
        record?.humanStartedAt,
    },
    {
      title: 'Finished',
      dataIndex: 'deletedAt',
      render: (deletedAt: string, record: CopytradeHistory) =>
        record?.humanDeletedAt,
    },
  ]

  if (![0, 404].includes(errorCopytradeHistoryRequest.code)) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          onChangeCopytradeHistoryList({
            limit: Number(searchParams.get('limit')) || 10,
            page: Number(searchParams.get('page')) || 1,
            filters: getFilters(),
          })
          setCopytradeHistoryRequestError({
            message: '',
            code: 0,
          })
        }}
      />
    )
  }
  const current = Number(searchParams.get('page')) || 1
  const pageSize = Number(searchParams.get('limit')) || 10
  return (
    <Table
      dataSource={copytradeHistory}
      bordered={true}
      scroll={{ x: true }}
      loading={loading}
      onRow={(record, rowIndex) => {
        return {
          onClick: (event) => {},
        }
      }}
      rowKey="id"
      columns={columns}
      pagination={{
        current,
        pageSize,
        showSizeChanger: true,
        total: totalCountOfCopytradeHistory,
      }}
      onChange={handleTableChange}
    />
  )
}
